import { Component, OnDestroy, ViewChild, TemplateRef, input, output, signal, effect } from '@angular/core';
import { Subject } from 'rxjs';
import { Integration } from '../../../../../models/integration';
import { IntegrationIdentifier } from '../../../../../models/integration-identifier';
import { IntegrationInformation } from 'src/app/models/integration-information';
import { Server } from '../../../../../models/server';
import { User } from '../../../../../models/user';
import { ClientIntegrationService } from 'src/app/services/client-integration.service';
import { ToastService } from 'src/app/services/toast.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ServerDropdownViewComponent } from 'src/app/features/lt-input/lt-dropdown/components/server-dropdown-view/server-dropdown-view.component';
import { LtScrollCardsComponent } from 'src/app/features/lt-shared-ui/lt-card/components/lt-scroll-cards/lt-scroll-cards.component';
import { EditIntegrationComponent } from '../edit-integration/edit-integration.component';
import { ServerService } from 'src/app/services/server.service';
import { IntegrationLockComponent } from '../integration-lock/integration-lock.component';

@Component({
  selector: 'app-integration-details-view',
  imports: [
    CommonModule,
    FormsModule,
    ClipboardModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDividerModule,
    MatIconModule,
    MatTooltipModule,
    ServerDropdownViewComponent,
    LtScrollCardsComponent,
    IntegrationLockComponent,
    EditIntegrationComponent,
  ],
  templateUrl: './integration-details-view.component.html',
  styleUrl: './integration-details-view.component.scss',
})
export class IntegrationDetailsViewComponent implements OnDestroy {
  destroyed$: Subject<boolean> = new Subject();
  integration = input.required<Integration>();
  integrations = input.required<Integration[]>();
  servers = input.required<Server[]>();
  identifiers = input<IntegrationInformation[] | []>([]);
  showIdentifiersComments = input<boolean>(false);
  savingInformation = input<boolean>(false);
  updateIntegration = output<Integration>();
  @ViewChild('editIntegrationDialog', { static: true }) editIntegrationDialog: TemplateRef<any>;
  @ViewChild('lockReason') public lockReasonComponent: IntegrationLockComponent;
  @ViewChild('editIntegrationName') public editIntegrationName: EditIntegrationComponent;
  public identifierCardTrackerFn = (item: IntegrationIdentifier) => `${item.integrationId}_${item.destinationName}_${item.identifierNameValue}`;
  selectedServer = signal<Server | null>(null);
  updatingIntegration = signal<boolean>(false);

  constructor(
    private clientService: ClientIntegrationService,
    private serverService: ServerService,
    private toastService: ToastService,
    private dialog: MatDialog,
    private user: User,
  ) { 
    effect(() => {
      this.updateSelectedServer();
    });
  }

  ngOnDestroy() {
    this.dialog.closeAll();
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  updateSelectedServer(): void {
    const currentIntegration = this.integration();
    const availableServers = this.servers();

    if (currentIntegration && currentIntegration.serverId && availableServers && availableServers.length > 0) {
      const matchingServer = availableServers.find(server => server.serverId === currentIntegration.serverId);
      this.selectedServer.set(matchingServer || null);
    } else {
      this.selectedServer.set(null);
    }
  }

  checkAccess(role: string[]): boolean {
    return this.user.checkAccess(role);
  }

  selectServer(s: Server): void {
    this.selectedServer.set(s);
  }

  changeServer(serverId: string): void {
    this.serverService
      .updateRunningServer(this.integration().integrationId, serverId)
      .subscribe({
        next: () => {
          this.toastService.toastCreate('Successfully moved integration to App Server ' + serverId, 'Success', {
            autoClose: false,
          });
          this.selectedServer.set(this.servers().find(s => s.serverId === serverId) || null);

          const updatedIntegration = { ...this.integration(), serverId: serverId };
          this.updateIntegration.emit(updatedIntegration);
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to move integration to App Server ' + serverId + '.', 'Danger', {
            autoClose: false,
          });
        },
      });
  }

  updateLock(lockReason: string): void {
    this.updatingIntegration.set(true);
    const currentIntegration = this.integration();
    const lockValue = !currentIntegration.locked;
    const lockMessage = lockValue ? 'locked' : 'unlocked';
    const lockReasonMessage = lockValue ? `Reason: ${lockReason}` : '';
    this.clientService.lockIntegration(currentIntegration.integrationId, lockValue, lockReason)
    .subscribe({
      next: () => {
        currentIntegration.locked = lockValue;
        currentIntegration.lockReason = lockReason;
        const updatedIntegration = { ...this.integration(), locked: lockValue };
        this.updateIntegration.emit(updatedIntegration);
        this.toastService.toastCreate(`Successfully ${lockMessage} integration. ${lockReasonMessage}`, 'Success', {
          autoClose: false,
        });
        this.updatingIntegration.set(false);
      },
      error: (_error) => {
        this.toastService.toastCreate(`Failed to ${lockValue ? 'lock' : 'unlock'} integration.`, 'Danger', {
          autoClose: false,
        });
        this.updatingIntegration.set(false);
      },
    });
  }

  updateRestrictIP(): void {
    const currentIntegration = this.integration();
    const restrictIPValue = currentIntegration.ipRestricted;
    const restrictIPMessage = restrictIPValue ? 'restricted' : 'unrestricted';

    this.clientService.restrictIPIntegration(currentIntegration.integrationId, restrictIPValue)
    .subscribe({
      next: () => {
        const updatedIntegration = { ...this.integration(), ipRestricted: restrictIPValue };
        this.updateIntegration.emit(updatedIntegration);
        this.toastService.toastCreate(`Successfully ${restrictIPMessage} ip for integration.`, 'Success', {
          autoClose: false,
        });
      },
      error: (_error) => {
        // Revert the checkbox, since ngModel already changed the value
        currentIntegration.ipRestricted = !restrictIPValue;
        this.toastService.toastCreate('Failed to update IP restriction for integration.', 'Danger', {
          autoClose: false,
        });
      },
    });
  }

  updateSelfHosted(): void {
    const currentIntegration = this.integration();
    const selfHostedValue = currentIntegration.selfHosted;
    const selfHostedMessage = selfHostedValue ? 'flagged integration as self hosted' : 'removed integration self hosted status.';

    this.clientService
      .selfHostIntegration(currentIntegration.integrationId, selfHostedValue)
      .subscribe({
        next: () => {
          const updatedIntegration = { ...this.integration(), selfHosted: selfHostedValue };
          this.updateIntegration.emit(updatedIntegration);
          this.toastService.toastCreate(`Successfully ${selfHostedMessage}`, 'Success', {
            autoClose: false,
          });
        },
        error: (_error) => {
          // Revert the checkbox, since ngModel already changed the value
          currentIntegration.selfHosted = !selfHostedValue;
          this.toastService.toastCreate('Failed to update self hosted status for integration.', 'Danger', {
            autoClose: false,
          });
        },
      });
  }

  openEditIntegrationDialog() {
    this.dialog.open(this.editIntegrationDialog);
  }

}
